/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import '@/auth/components/ResetPasswordForm/ResetPasswordForm.css';
import { useResetPasswordForm } from '@/auth/hooks/components/useResetPasswordForm';
import { ResetPasswordFormProps } from '@/auth/types';
import { ChevronDown as ChevronLeft } from '@adobe-commerce/elsie/icons';
import { Form, Button } from '@/auth/components';
import { useInLineAlert } from '@/auth/hooks/useInLineAlert';
import { Header, InLineAlert } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { simplifyTransformAttributesForm } from '@/auth/lib/simplifyTransformAttributesForm';
import { DEFAULT__RESET_PASSWORD_EMAIL_FIELD } from '@/auth/configs/defaultCreateUserConfigs';

export const ResetPasswordForm: FunctionComponent<
  ResetPasswordFormProps & HTMLAttributes<HTMLDivElement>
> = ({
  formSize = 'default',
  routeSignIn,
  setActiveComponent,
  onErrorCallback,
  onSuccessCallback,
  slots,
  ...props
}) => {
    const translations = useText({
      title: 'Auth.ResetPasswordForm.title',
      buttonPrimary: 'Auth.ResetPasswordForm.buttonPrimary',
      buttonSecondary: 'Auth.ResetPasswordForm.buttonSecondary',
      formAriaLabel: 'Auth.ResetPasswordForm.formAriaLabel',
    });

    const { inLineAlertProps, handleSetInLineAlertProps } = useInLineAlert();

    const { isLoading, submitResetPassword, redirectToSignInPage } =
      useResetPasswordForm({
        routeSignIn,
        setActiveComponent,
        onErrorCallback,
        onSuccessCallback,
        handleSetInLineAlertProps,
      });

    const slotContext = { isLoading };

    return (
      <div
        {...props}
        className={classes([
          'auth-reset-password-form',
          `auth-reset-password-form--${formSize}`,
        ])}
        data-testid="resetPasswordForm"
      >
        <Slot
          name="Title"
          data-testid="title"
          slot={slots?.Title}
          context={{ ...slotContext }}
        >
          <Header
            title={translations.title}
            divider={false}
            className="auth-reset-password-form__title"
          />
        </Slot>
        {inLineAlertProps.text ? (
          <InLineAlert
            className="auth-reset-password-form__notification"
            type={inLineAlertProps.type}
            variant="secondary"
            heading={inLineAlertProps.text}
            icon={inLineAlertProps.icon}
          />
        ) : null}
        <Form
          aria-labelledby={translations.formAriaLabel}
          name="resetPassword_form"
          className="auth-reset-password-form__form"
          onSubmit={submitResetPassword}
          loading={isLoading}
          fieldsConfig={simplifyTransformAttributesForm(
            DEFAULT__RESET_PASSWORD_EMAIL_FIELD
          )}
          formSlot={{
            name: 'Form',
            slot: slots?.Form,
            context: slotContext,
            dataTestId: 'form',
          }}
        >
          <Slot
            name="Buttons"
            className="auth-reset-password-form__buttons"
            data-testid="buttons"
            slot={slots?.Buttons}
            context={{ ...slotContext }}
          >
            <Button
              className="auth-reset-password-form__buttons--signin"
              type="button"
              variant="tertiary"
              style={{ padding: '0' }}
              icon={<ChevronLeft style={{ transform: 'rotate(90deg)' }} />}
              buttonText={translations.buttonSecondary}
              enableLoader={false}
              onClick={redirectToSignInPage}
            />
            <Button
              type="submit"
              buttonText={translations.buttonPrimary}
              variant="primary"
              enableLoader={isLoading}
            />
          </Slot>
        </Form>
        <div id="requestPasswordResetEmail" />
      </div>
    );
  };
